import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import DayPicker from '@rio-cloud/rio-uikit/components/datepicker/DayPicker';
import { endOfDay, endOfMonth, startOfDay, startOfMonth, startOfYear, sub } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import { t } from '@/lib/utils';

import { DateFrame, type TimeFrame, type TimePickerProps, TimePickerType } from './types';

const timeFrameLabelMap: Record<DateFrame, string> = {
    [DateFrame.TODAY]: 'general.filter.today',
    [DateFrame.YESTERDAY]: 'general.filter.yesterday',
    [DateFrame['7DAYS']]: 'general.filter.last7Days',
    [DateFrame.MONTH]: 'general.filter.thisMonth',
    [DateFrame.LAST_MONTH]: 'general.filter.LastMonth',
    [DateFrame.LAST_3MONTHS]: 'general.filter.last3Months',
    [DateFrame.LAST_6MONTHS]: 'general.filter.last6Months',
    [DateFrame.YEAR]: 'general.filter.thisYear',
    [DateFrame.CUSTOM]: 'general.filter.customDateRange',
};

export const timeFramePickerLabel = (time: DateFrame, intl: IntlShape): string =>
    intl.formatMessage({ id: timeFrameLabelMap[time] });

const getRelativeDateRange = (frame: DateFrame): [Date, Date] => {
    const now = new Date();
    switch (frame) {
        case DateFrame.TODAY:
            return [startOfDay(now), now];
        case DateFrame.YESTERDAY: {
            const day = sub(now, { days: 1 });
            return [startOfDay(day), endOfDay(day)];
        }
        case DateFrame['7DAYS']:
            return [startOfDay(sub(now, { days: 7 })), now];
        case DateFrame.MONTH:
            return [startOfMonth(now), now];
        case DateFrame.LAST_MONTH: {
            const lastMonth = sub(now, { months: 1 });
            return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
        }
        case DateFrame.LAST_3MONTHS: {
            const start = sub(now, { months: 2 });
            return [startOfMonth(start), now];
        }
        case DateFrame.LAST_6MONTHS: {
            const start = sub(now, { months: 5 });
            return [startOfMonth(start), now];
        }
        case DateFrame.YEAR:
            return [startOfYear(now), now];
        default:
            return [now, now];
    }
};

const getCustomRange = (
    start: Date | undefined,
    end: Date | undefined,
    timeFrame: TimeFrame,
    selectByMonth?: boolean
): [Date, Date] => {
    const fallbackStart = timeFrame.startDate ?? new Date();
    const fallbackEnd = timeFrame.endDate ?? new Date();

    const s = start ?? fallbackStart;
    const e = end ?? fallbackEnd;

    return [selectByMonth ? startOfMonth(s) : startOfDay(s), selectByMonth ? endOfMonth(e) : endOfDay(e)];
};

export const TimePicker = ({
    timePickerType,
    timeFrame,
    setTimeFrame,
    callback,
    disabled,
    selectByMonth,
}: TimePickerProps) => {
    const intl = useIntl();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>(timeFrame.frame.timeLabel);
    const [isCustomDate, setIsCustomDate] = useState<boolean>(timeFrame.frame.timeEnum === DateFrame.CUSTOM);

    const handleFrameSelection = useCallback(
        (frame: DateFrame, customStart?: Date, customEnd?: Date) => {
            let startDate: Date;
            let endDate: Date;

            if (frame === DateFrame.CUSTOM) {
                [startDate, endDate] = getCustomRange(customStart, customEnd, timeFrame, selectByMonth);
                setIsCustomDate(true);
            } else {
                [startDate, endDate] = getRelativeDateRange(frame);
                setIsCustomDate(false);
            }

            const label = timeFramePickerLabel(frame, intl);
            setSelectedTimeFrame(label);

            setTimeFrame({
                startDate,
                endDate,
                frame: {
                    timeEnum: frame,
                    timeLabel: label,
                },
            });

            callback(startDate, endDate);
        },
        [timeFrame, selectByMonth, setTimeFrame, callback]
    );

    const timeFrameOptions = useMemo(() => {
        const options =
            timePickerType === TimePickerType.SHORT
                ? [DateFrame.TODAY, DateFrame.YESTERDAY, DateFrame['7DAYS'], DateFrame.MONTH, DateFrame.LAST_MONTH]
                : [
                      DateFrame.MONTH,
                      DateFrame.LAST_MONTH,
                      DateFrame.LAST_3MONTHS,
                      DateFrame.LAST_6MONTHS,
                      DateFrame.YEAR,
                  ];

        return [
            ...options.map(frame => ({
                value: timeFramePickerLabel(frame, intl),
                onSelect: () => handleFrameSelection(frame),
            })),
            { divider: true },
            {
                value: timeFramePickerLabel(DateFrame.CUSTOM, intl),
                onSelect: () => handleFrameSelection(DateFrame.CUSTOM),
            },
        ];
    }, [timePickerType, handleFrameSelection]);

    const getDisabledDays =
        (isStart: boolean, timeFrame: TimeFrame, selectByMonth?: boolean): ((date: Date) => boolean) =>
        (date: Date): boolean => {
            const now = new Date();

            if (selectByMonth) {
                const dateMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                const nowMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                if (dateMonth > nowMonth) {
                    return true;
                }

                if (isStart && timeFrame.endDate) {
                    const endMonth = new Date(timeFrame.endDate.getFullYear(), timeFrame.endDate.getMonth(), 1);
                    if (dateMonth > endMonth) {
                        return true;
                    }
                }
                if (!isStart && timeFrame.startDate) {
                    const startMonth = new Date(timeFrame.startDate.getFullYear(), timeFrame.startDate.getMonth(), 1);
                    if (dateMonth < startMonth) {
                        return true;
                    }
                }
            } else {
                if (date > now) {
                    return true;
                }

                if (isStart && timeFrame.endDate && date > timeFrame.endDate) {
                    return true;
                }
                if (!isStart && timeFrame.startDate && date < timeFrame.startDate) {
                    return true;
                }
            }

            return false;
        };

    const handleDateChange = (date: Date | undefined, isStart: boolean, timeFrame: TimeFrame) => {
        handleFrameSelection(
            DateFrame.CUSTOM,
            isStart ? date : timeFrame.startDate,
            !isStart ? date : timeFrame.endDate
        );
    };

    const getLocaleDateFormat = (locale: string): string => {
        const parts = new Intl.DateTimeFormat(locale).formatToParts(new Date());

        return parts
            .map(part => {
                switch (part.type) {
                    case 'day':
                        return 'dd';
                    case 'month':
                        return 'MM';
                    case 'year':
                        return 'yyyy';
                    default:
                        return part.value;
                }
            })
            .join('');
    };

    const getLocaleMonthYearFormat = (locale: string): string => {
        const parts = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
        }).formatToParts(new Date());

        return parts
            .map(part => {
                switch (part.type) {
                    case 'month':
                        return 'MM';
                    case 'year':
                        return 'yyyy';
                    default:
                        return part.value;
                }
            })
            .join('');
    };

    const renderDayPicker = (labelId: string, value: Date | undefined, isStart: boolean) => (
        <div style={{ width: '120px' }} data-testid={isStart ? 'date-picker-start' : 'date-picker-end'}>
            <label>
                <FormattedMessage id={labelId} />
            </label>
            <DayPicker
                className='margin-bottom-0'
                placeholder={t('general.filter.selectDate')}
                disabledDays={getDisabledDays(isStart, timeFrame, selectByMonth)}
                value={value}
                dateFormat={selectByMonth ? getLocaleMonthYearFormat(intl.locale) : getLocaleDateFormat(intl.locale)}
                onChange={date => handleDateChange(date, isStart, timeFrame)}
                alignRight={!isStart}
                locale={intl.locale}
            />
        </div>
    );

    return (
        <div className='display-flex gap-10'>
            <div style={{ marginTop: '13px' }}>
                <ButtonDropdown
                    disabled={disabled}
                    noCaret
                    title={
                        <div className='display-flex align-items-center gap-5'>
                            <span className='rioglyph rioglyph-filter-active' />
                            <span>{selectedTimeFrame}</span>
                        </div>
                    }
                    items={timeFrameOptions}
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                />
            </div>

            {isCustomDate && (
                <div className='display-flex gap-10' style={{ marginTop: '-11px' }}>
                    {renderDayPicker('general.from', timeFrame.startDate, true)}
                    {renderDayPicker('general.to', timeFrame.endDate, false)}
                </div>
            )}
        </div>
    );
};
